/**
 * The one loading primitive the front end uses.
 *
 * Three states and no fourth: `loading`, `error`, `data`. `05-reporting-marking.md` §B9
 * requires a skeleton while loading and a fail-closed banner on error, and forbids
 * rendering a figure before its value is known — so a caller can never be handed a
 * half-populated object that looks like data.
 *
 * A superseded response cannot land: the effect marks itself stale on cleanup, so a slow
 * fetch for the previous official can never overwrite the current one's panel.
 */

import { useEffect, useRef, useState } from "react";

export type AsyncState<T> =
  | { status: "loading" }
  | { status: "error"; error: Error }
  | { status: "ready"; data: T };

export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });

  useEffect(() => {
    let live = true;
    setState({ status: "loading" });
    load().then(
      (data) => {
        if (live) setState({ status: "ready", data });
      },
      (error: unknown) => {
        if (live) {
          setState({
            status: "error",
            error: error instanceof Error ? error : new Error("The request failed."),
          });
        }
      },
    );
    return () => {
      live = false;
    };
    // The caller states its own dependencies; `load` is deliberately not one of them,
    // because an inline closure would re-fetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

/**
 * {@link AsyncState}, with the moment a ready answer arrived. RUN 82C.
 *
 * `readAt` is when the read that is on screen ANSWERED — not when it was asked, and not the time
 * of the last render. It is what an `As Of` stamp is made from.
 */
export type RefreshingAsyncState<T> =
  | { status: "loading" }
  | { status: "error"; error: Error }
  | { status: "ready"; data: T; readAt: Date };

/** The same values in the same places — how a refresh is told apart from a new question. */
function sameValues(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((value, index) => Object.is(value, b[index]));
}

/**
 * RUN 82G — how long a read may be out before a refresh of the same question stops waiting for it:
 * **ten minutes**.
 *
 * Skipping a refresh while a read of the question is out is right for as long as that read may
 * still answer. `fetch` has no deadline of its own, though (`api/client.ts` sets none), and a
 * request stranded on a venue connection that died under it can stay out for a very long time. A
 * hook that went on waiting for it would never ask again, and the desk would sit on its last
 * answer until somebody reloaded the page. So a read out for longer than this no longer holds a
 * refresh back, and the next one asks again. Ten minutes is also when the desk's `As Of` stamp
 * starts to say `Refresh Failing`, so the desk starts asking again at the moment it starts to say
 * it needs to. If the stranded read does answer after all, its answer lands only if nothing newer
 * has — which is what the numbers below are for.
 */
export const STRANDED_READ_MS = 10 * 60_000;

/**
 * RUN 82C — {@link useAsync} for a read that is asked AGAIN while its answer is on screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS A SECOND HOOK, AND NOT AN OPTION ON THE FIRST
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `useAsync` resets to `loading` whenever its dependencies change, and it is right to: a new
 * dependency is a new question — another official, another day — and the answer to the last one
 * must not stand in for it while the new one is fetched. The Command Desk's independent QA
 * (RUN 82C) found the other case, which that reset gets wrong: the SAME question asked again,
 * because the answer may have moved. The Technical Delegate marks a day locked on TO
 * Appointments; the desk he left open should say so without a reload, and saying so must not
 * mean the crew lines, the chips and the locked record vanishing into skeleton bars every five
 * minutes while the venue connection thinks about it. Changing `useAsync` itself would have
 * changed that behaviour under every other module that calls it — twenty-five files when this
 * was written — and those rely on the reset.
 *
 * So there are two kinds of change here, and they are kept apart:
 *
 *   · **`deps` changed — a new question.** Exactly `useAsync`: `loading`, then the answer.
 *   · **Only `refreshKey` changed — the same question again.** Nothing is reset. The last ready
 *     answer stays on screen, with its own `readAt`, until the new one lands and replaces it. A
 *     refresh that FAILS keeps that last good answer, and its `readAt` stays where it was, which
 *     is how an `As Of` stamp comes to say that the figures under it are old. A question that has
 *     never been answered is not hidden by a refresh either: a failure still shows as `error`,
 *     and the refresh that succeeds replaces it.
 *
 * A superseded response still cannot land, for the reason `useAsync` gives: each run marks itself
 * stale on cleanup, whether a new question or a newer refresh superseded it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 82G — ONE READ OUT AT A TIME, AND ANSWERS APPLIED BY NUMBER
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The paragraph above is superseded. The second independent QA of the Command Desk (L3) found two
 * ways that rule lost answers or multiplied reads:
 *
 *   · **A storm.** A tab coming back, a window taking focus and the five-minute mark can all land
 *     within a second of each other, and each one asked again while the last was still out —
 *     three identical reads over a venue connection for one answer.
 *   · **A miss.** Marking every earlier run stale threw away a good answer to the SAME question
 *     just because a later refresh had been asked: if that later one then failed, or hung, the
 *     screen kept the older answer still, although a newer one had arrived and been discarded.
 *
 * So the rules are now these, and `useAsync.test.tsx` pins each of them:
 *
 *   · **A refresh is skipped while a read of the same question is already out** — for up to
 *     {@link STRANDED_READ_MS}. The read that is out will answer it. A new question is always
 *     asked.
 *   · **Every read is numbered as it is asked**, and an answer is applied when it answers the
 *     question now on screen — compared by value, as a refresh is told from a new question — and
 *     is newer than the answer showing. Asked about Thursday, then Friday, then Thursday again, the
 *     first Thursday answer lands rather than being thrown away, and a later-numbered one replaces
 *     it; an older one arriving after a newer one does not.
 *   · **A refusal lands only when no newer read of the question is still out**, and still never
 *     takes a good answer to the question off the screen.
 */
export function useRefreshingAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  refreshKey: string | number,
): RefreshingAsyncState<T> {
  const [state, setState] = useState<RefreshingAsyncState<T>>({ status: "loading" });
  /**
   * The dependencies of the last new question — the question the state on screen answers, or is
   * being answered. A ref, because it is bookkeeping between two runs of the effect and must not
   * itself cause a render.
   */
  const asked = useRef<readonly unknown[] | null>(null);
  /** RUN 82G — the number the next read is given. Reads of every question share the one count. */
  const nextNumber = useRef(1);
  /** RUN 82G — the reads still out: each one's number, the question it asked, and when. */
  const outstanding = useRef<{ number: number; question: readonly unknown[]; askedAt: number }[]>(
    [],
  );
  /** RUN 82G — the number of the answer on screen; 0 while none of the current question's is. */
  const shown = useRef(0);

  useEffect(() => {
    const refresh = asked.current !== null && sameValues(asked.current, deps);
    if (!refresh) {
      asked.current = deps;
      shown.current = 0;
      setState({ status: "loading" });
    } else if (
      outstanding.current.some(
        (read) =>
          sameValues(read.question, deps) && Date.now() - read.askedAt <= STRANDED_READ_MS,
      )
    ) {
      // The read already out asks exactly this, and its answer will be applied when it lands.
      return;
    }

    const number = nextNumber.current;
    nextNumber.current += 1;
    const question = deps;
    outstanding.current = [...outstanding.current, { number, question, askedAt: Date.now() }];

    /** Takes this read off the list, and says whether its outcome may still reach the screen. */
    const lands = (): boolean => {
      outstanding.current = outstanding.current.filter((read) => read.number !== number);
      return (
        asked.current !== null && sameValues(asked.current, question) && number > shown.current
      );
    };

    load().then(
      (data) => {
        if (!lands()) return;
        shown.current = number;
        setState({ status: "ready", data, readAt: new Date() });
      },
      (error: unknown) => {
        if (!lands()) return;
        // A newer read of the same question is still out: its outcome, not this one, decides.
        if (
          outstanding.current.some(
            (read) => read.number > number && sameValues(read.question, question),
          )
        ) {
          return;
        }
        const failed: RefreshingAsyncState<T> = {
          status: "error",
          error: error instanceof Error ? error : new Error("The request failed."),
        };
        // The one line that is the point of this hook: a refresh that fails is not allowed to
        // take a good answer off the screen. (RUN 82G: `ready` here is always this question's —
        // a new question resets to `loading` before anything of its own can land.)
        setState((current) => (current.status === "ready" ? current : failed));
      },
    );
    // As `useAsync`: the caller states the dependencies, and `load` is not one of them. The
    // refresh key is appended, so a new key re-runs the read and a new question is still told
    // apart from it above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refreshKey]);

  return state;
}
